import { useState, FC, useEffect, useContext, useCallback } from "react";
import useRouter from "@stadium/hooks/lib/use-custom-route";
import { useSelector } from "react-redux";

import { getRecipientSendPointsRoute } from "@stadium/shared/src/constants/routes";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";
import { ExternalButton, InternalButton } from "@sharedComponents/buttons";

import { useJuneSoEventTracking } from "@stadium/services/june-analytics/hooks/use-june-so-event-tracking";
import { useWithAnalytics } from "@stadium/services/analytics/hooks/use-with-analytics";
import {
  ANALYTICS_EVENT_ACTION_SELECTED,
} from "@stadium/services/analytics/constants/default";
import { HEADER_ANALYTICS_TARGET } from "@stadium/shared/src/constants/analytics/header";

import ControlledTooltip from "@stadium/ui/src/components/tooltip/controlled-tooltip";
import RadioInput from "@stadium/ui/src/components/form/RadioInput";
import Multimedia from "@sharedComponents/multimedia";
import Label from "@stadium/ui/src/components/form/Label";
import Modal from "@stadium/ui/src/components/ModalNew";
import {
  ModalHeader,
  ModalFooter,
  CloseButton,
  ModalBody,
  ModalContent,
  ModalDialog,
} from "@stadium/ui/src/components/ModalNew/utils";

import { useSendItemsContext } from "@contexts/send-items-context";
import { useStoreContext } from "@components/store/provider";

import { useHandleSendToManyToggle } from "@hooks/cart/use-handle-send-to-many-toggle";
import { useWorkspaceContext } from "@contexts/workspace-context";

import SendToManyCountrySelectorModal from "@sharedComponents/cart/send-to-many/send-to-many-country-selector-modal";

import { getCartOrderId, getCartProductZoneList } from "@selectors/cart";
import { getStoreBasePathWithoutCountryCode } from "@constants/routes";
import {
  getAllowSendPointsFromStorefront,
  getIsLockerShop,
  getStoreCurrencyName,
} from "@selectors/store";

import {
  SEND_ITEMS_SELECTION_OPTINS,
  SEND_TO_MYSELF,
  SEND_ITEMS,
  SEND_TO_OTHERS,
} from "@constants/navigation";

import {
  DEFAULT_SERVICES,
  SHOP_FRONT,
  SEND_TO_MYSELF as SEND_TO_MYSELF_JUNE_SO,
  SEND_TO_OTHER,
  DONE,
} from "@constants/june-so";

import styles from "./main.module.scss";

import iconGift from "@images/svg/gift-icon.svg";
import iconGiftBlue from "@images/svg/gift-icon-blue.svg";

type SelectItemsRadioType = "send-to-myself" | "send-to-others";

const SendItemsSelection: FC = () => {
  const { query, asPath, replace } = useRouter();
  const sendItems = query[SEND_ITEMS];
  const { permissions } = useWorkspaceContext();
  const orderId = useSelector(getCartOrderId);
  const cartProductZoneList = useSelector(getCartProductZoneList);
  const storeCurrencyName = useSelector(getStoreCurrencyName);
  const isLockerShop = useSelector(getIsLockerShop);
  const allowSendPointsFromStorefront =
    useSelector(getAllowSendPointsFromStorefront) && !isLockerShop && permissions.canSendPoints;
  const { updateOrderStatus } = useHandleSendToManyToggle();

  const { trackEvent } = useJuneSoEventTracking({ services: DEFAULT_SERVICES });
  const withAnalytics = useWithAnalytics();

  const { isMobileDevice } = useContext(CurrentDeviceContext);
  const { isGlobalCountry, storeCode, storeNumber, workspaceSlug, isNewRoute } = useStoreContext();

  const {
    isSendToMyself,
    sendItemsSelection,
    setSendItemsSelection,
    isCountrySelectorModalOpen,
    openCountrySelectorModal,
  } = useSendItemsContext();

  const [sendItemsRadio, setSendItemsRadio] = useState<SelectItemsRadioType>(sendItemsSelection);
  const [isItemSelectionModalOpen, setIsItemSelectionModalOpen] = useState<boolean>(false);
  const [toolTipVisibleStatus, setToolTipVisibleStatus] = useState<boolean>(false);

  const openItemSelectionModal = () => setIsItemSelectionModalOpen(true);
  const closeItemSelectionModal = () => setIsItemSelectionModalOpen(false);

  const getTooltipVisibleStatus = (status) => setToolTipVisibleStatus(status);

  useEffect(() => {
    if (sendItems == "true") openItemSelectionModal();
  }, [sendItems]);

  useEffect(() => {
    setSendItemsRadio(sendItemsSelection);
  }, [sendItemsSelection]);

  const trackModeSelection = useCallback(
    (target: string) => {
      withAnalytics(undefined, {
        track: true,
        target,
        action: ANALYTICS_EVENT_ACTION_SELECTED,
      })();
    },
    [withAnalytics],
  );

  const onRadioChange = ({ value }) => {
    setSendItemsRadio(value);
    trackEvent({
      componentName: SHOP_FRONT,
      target: value === SEND_TO_MYSELF ? SEND_TO_MYSELF_JUNE_SO : SEND_TO_OTHER,
    });
    trackModeSelection(
      value === SEND_TO_MYSELF
        ? HEADER_ANALYTICS_TARGET.SELF
        : HEADER_ANALYTICS_TARGET.OTHER,
    );
  };

  const doneHandler = () => {
    isMobileDevice && closeItemSelectionModal();
    if (
      sendItemsSelection !== sendItemsRadio &&
      sendItemsRadio === SEND_TO_MYSELF &&
      cartProductZoneList.length > 1
    ) {
      setSendItemsRadio(SEND_TO_OTHERS);
      openCountrySelectorModal();
      return;
    }

    if (sendItemsRadio === SEND_TO_MYSELF && isGlobalCountry) {
      replace(
        getStoreBasePathWithoutCountryCode({ storeCode, storeNumber, workspaceSlug, isNewRoute }),
        undefined,
        {
          shallow: true,
        },
      );
    }

    setSendItemsSelection(sendItemsRadio);
    updateOrderStatus(sendItemsRadio);
    if (sendItems) {
      const trimmedPath = asPath.split("?")[0];
      replace(asPath, trimmedPath, { shallow: true });
    }
    trackEvent({
      componentName: SHOP_FRONT,
      target: DONE,
    });
  };

  const radioGroupProps = {
    name: "send-items-selection",
    onChange: onRadioChange,
  };

  const renderItemSelectionIcon = () => (
    <div
      {...(isMobileDevice && { onClick: openItemSelectionModal })}
      className={styles.sendToManyBlock}
    >
      <Multimedia
        type="image"
        src={isSendToMyself ? iconGift : iconGiftBlue}
        width={30}
        height={32}
        alt="Send Gift"
        className={""}
      />
      <span
        className={`${styles.iconDropDown} ${toolTipVisibleStatus ? styles.iconDropDownUp : ""}`}
      />
    </div>
  );

  const renderTippyContent = () => (
    <div className={styles.tippyBodyContent}>
      <div className={styles.radioBlock}>
        {Object.values(SEND_ITEMS_SELECTION_OPTINS).map(({ key, value, label }) => (
          <RadioInput
            key={key}
            id={key}
            value={value}
            checked={sendItemsRadio === key}
            classes={styles.inputRadioBlock}
            label={
              <Label
                htmlFor={key}
                iconType="radioOutline"
                styles={styles}
                className={styles.inputLabel}
              >
                <div>
                  <div className={styles.value}>{label}</div>
                  {key === SEND_TO_OTHERS && (
                    <div className={styles.info}>
                      Add items to your bag to send to others, and you'll add your recipients'
                      emails in the next step. They’ll input their sizes (if applicable) and
                      addresses when they redeem, or you can input their info to make it a surprise
                      order.
                    </div>
                  )}
                </div>
              </Label>
            }
            {...radioGroupProps}
          />
        ))}
      </div>
    </div>
  );

  const renderModalBody = () => renderTippyContent();

  const renderSendPointsCta = () => {
    if (!allowSendPointsFromStorefront) return null;

    return (
      <div className={styles.sendPointsBlock}>
        Or, send a budget so recipients can shop
        <ExternalButton
          href={getRecipientSendPointsRoute(storeNumber, workspaceSlug)}
          className={styles.sendPointsLink}
          eventLocationAnalytics={{
            track: true,
            target: HEADER_ANALYTICS_TARGET.SEND_POINTS_LINK,
          }}
        >
          Send {storeCurrencyName}
        </ExternalButton>
      </div>
    );
  };

  const renderContentInModal = () =>
    isItemSelectionModalOpen && (
      <Modal
        closeModal={closeItemSelectionModal}
        mobileBottomDrawer={true}
        className={styles.selectionModal}
      >
        <ModalDialog>
          <ModalContent>
            <ModalHeader className={styles.modalHeader}>
              <div className={styles.heading}>How do you want to use this shop?</div>
              <CloseButton onClick={closeItemSelectionModal} className={styles.iconClose} />
            </ModalHeader>
            <ModalBody className={styles.modalBody}>{renderModalBody()}</ModalBody>
            <ModalFooter className={`${styles.modalFooter} ${styles.reverseFooter}`}>
              <InternalButton
                disableOnLoad
                styleType="primary"
                onClick={doneHandler}
                className={styles.footerButton}
              >
                save
              </InternalButton>
              {renderSendPointsCta()}
            </ModalFooter>
          </ModalContent>
        </ModalDialog>
      </Modal>
    );

  const renderContentInTooltip = () => (
    <ControlledTooltip
      isDefaultTooltipVisible={sendItems == "true"}
      placement="bottom"
      interactive={true}
      isCrossIconRequired={false}
      heading="How do you want to use this shop? "
      bodyContent={renderTippyContent()}
      footerCloseButtonCtaText="Save"
      footerCloseButtonAction={doneHandler}
      tipContainerClass={`${styles.tippyContainer} ${
        allowSendPointsFromStorefront ? "" : styles.tippyPaddingBottom
      }`}
      actionBlockClass={styles.footerLinkBlock}
      actionButtonClass={styles.btnSave}
      actionButtonStyleType="primary"
      containerClassName={styles.toolTipWrapper}
      headingClass={styles.tipHeading}
      isLeftNextImage={false}
      getTippyInstanceStatus={getTooltipVisibleStatus}
      renderFooter={({ cta, className }) => (
        <div className={className}>
          {cta}
          {renderSendPointsCta()}
        </div>
      )}
    >
      {renderItemSelectionIcon()}
    </ControlledTooltip>
  );

  return (
    <>
      {isMobileDevice ? (
        <>
          {renderItemSelectionIcon()}
          {renderContentInModal()}
        </>
      ) : (
        renderContentInTooltip()
      )}
      {orderId && isCountrySelectorModalOpen && <SendToManyCountrySelectorModal />}
    </>
  );
};
export default SendItemsSelection;
